import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, Search, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LogoMark } from "../components/Logo";

const STATUS_STYLES = {
  new: "bg-[#C68B4E] text-white",
  contacted: "bg-[#7A8B6F] text-white",
  closed: "bg-[#2B2B2B]/20 text-[#2B2B2B]",
};
const STATUSES = ["new", "contacted", "closed"];

const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  const load = () => {
    api.get("/admin/quotes")
      .then(({ data }) => setQuotes(data))
      .catch(() => toast.error("Could not load submissions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && user.id) load();
  }, [user]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const filtered = quotes.filter((x) => {
    const matchesSearch = `${x.name} ${x.email} ${x.project_type} ${x.address}`.toLowerCase().includes(q.toLowerCase());
    const matchesFilter = filter === "all" || x.status === filter;
    return matchesSearch && matchesFilter;
  });

  const counts = {
    all: quotes.length,
    new: quotes.filter((x) => x.status === "new").length,
    contacted: quotes.filter((x) => x.status === "contacted").length,
    closed: quotes.filter((x) => x.status === "closed").length,
  };

  if (!user || user === null) {
    return <div className="min-h-screen bg-[#F7F3EC] flex items-center justify-center text-[#5C5C5C]">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F3EC]" data-testid="admin-dashboard">
      <header className="bg-[#2B2B2B] text-[#F7F3EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="inline-flex items-center gap-2.5">
            <div className="bg-[#F7F3EC] p-1.5 inline-flex items-center justify-center"><LogoMark size={30} /></div>
            <div className="flex flex-col justify-center leading-tight">
              <div className="font-extrabold tracking-tight">Bright Oak Admin</div>
              <div className="text-xs text-[#F7F3EC]/60">{user.email}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:text-[#C68B4E] transition-colors" data-testid="admin-logout-button">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Inbox size={28} className="text-[#C68B4E]" strokeWidth={1.5} />
          <h1 className="text-3xl font-extrabold tracking-tight">Quote Requests</h1>
        </div>

        {/* Filters + search */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                  filter === s ? "bg-[#2B2B2B] text-[#F7F3EC]" : "bg-white border border-[#2B2B2B]/15 hover:border-[#C68B4E]"
                }`}
              >
                {s} ({counts[s]})
              </button>
            ))}
          </div>
          <div className="relative md:ml-auto md:w-72">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search submissions..."
              data-testid="admin-search-input"
              className="w-full bg-white border border-[#2B2B2B]/15 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C68B4E]"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-[#5C5C5C] py-16 text-center">Loading submissions...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#2B2B2B]/10 p-16 text-center" data-testid="admin-empty">
            <Inbox size={40} className="text-[#2B2B2B]/20 mx-auto" />
            <p className="text-[#5C5C5C] mt-4">No quote requests found.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {filtered.map((qt) => (
              <div key={qt.id} data-testid={`admin-quote-${qt.id}`} className="bg-white border border-[#2B2B2B]/10 p-6 lg:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-bold">{qt.name}</h3>
                      <span className={`text-[0.6rem] font-bold uppercase tracking-widest px-3 py-1 ${STATUS_STYLES[qt.status]}`}>{qt.status}</span>
                      <span className="text-xs bg-[#7A8B6F]/15 text-[#7A8B6F] font-bold uppercase tracking-widest px-3 py-1">{qt.project_type}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-[#5C5C5C]">
                      <a href={`mailto:${qt.email}`} className="flex items-center gap-2 hover:text-[#C68B4E]"><Mail size={15} /> {qt.email}</a>
                      <a href={`tel:${qt.phone}`} className="flex items-center gap-2 hover:text-[#C68B4E]"><Phone size={15} /> {qt.phone}</a>
                      <span className="flex items-center gap-2"><MapPin size={15} /> {qt.address}</span>
                    </div>
                    <p className="text-sm text-[#2B2B2B] mt-4 leading-relaxed bg-[#F7F3EC] p-4 border-l-2 border-[#C68B4E]">{qt.description}</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-[#5C5C5C]">
                      <span><strong>Budget:</strong> {qt.budget_range}</span>
                      <span><strong>Received:</strong> {new Date(qt.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex lg:flex-col gap-2 shrink-0">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => changeStatus(qt.id, s)}
                        disabled={qt.status === s}
                        data-testid={`admin-set-${s}-${qt.id}`}
                        className={`text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors ${
                          qt.status === s ? "bg-[#2B2B2B] text-white cursor-default" : "border border-[#2B2B2B]/20 hover:bg-[#C68B4E] hover:text-white hover:border-[#C68B4E]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
